import { useState } from 'react';
import type * as React from 'react';
import { useCreate } from '@refinedev/core';
import { useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { Page, Notice } from '@/components/page';
import { DataTable, type Column } from '@/components/data-table';
import { EmptyState } from '@/components/states';
import { Button } from '@/components/ui/button';
import { Badge, Mono } from '@/components/ui/primitives';
import { RESOURCES, type AdminWizardSummary, type WizardStatus } from '@/api/resources';
import { useAdminList } from '@/hooks/use-admin-resource';
import { notify } from '@/lib/notify';
import { formatDateTime, formatNumber } from '@/lib/format';
import { useDelete } from '@refinedev/core';
import { Trash2 } from 'lucide-react';
import { ConfirmDialog } from '@/components/confirm-dialog';

const STATUS_TONE: Record<WizardStatus, 'caution' | 'success' | 'neutral'> = {
  DRAFT: 'caution',
  PUBLISHED: 'success',
  ARCHIVED: 'neutral',
};

/**
 * Wizard versions (06 §2).
 *
 * ⚠️ A published version is immutable: every child records the version that
 * produced its answers, so editing one in place would change the meaning of
 * data already stored. "Edit" on a published row therefore creates the next
 * draft rather than opening it, and the button says so.
 */
export function WizardListPage(): React.JSX.Element {
  const { rows, isLoading, error, refetch, invalidate } = useAdminList<AdminWizardSummary>(
    RESOURCES.wizards,
  );
  const { mutateAsync: create } = useCreate();
  const [creating, setCreating] = useState(false);
  const [discarding, setDiscarding] = useState<AdminWizardSummary | null>(null);
  const [discardPending, setDiscardPending] = useState(false);
  const { mutateAsync: deleteOne } = useDelete();
  const navigate = useNavigate();

  const createDraft = async (key: string): Promise<void> => {
    setCreating(true);
    try {
      const created = await create({
        resource: RESOURCES.wizards,
        values: { key },
        successNotification: false,
        errorNotification: false,
      });
      notify.success('Draft created', 'Pre-filled from the latest version.');
      invalidate();
      const id = (created.data as { id?: string } | undefined)?.id;
      if (id) void navigate(`/wizards/${id}`);
    } catch (err) {
      notify.error(err, 'The draft was not created');
    } finally {
      setCreating(false);
    }
  };

  const keys = [...new Set((rows ?? []).map((row) => row.key))];
  const primaryKey = keys[0] ?? 'child_onboarding';

  const columns: Array<Column<AdminWizardSummary>> = [
    {
      key: 'key',
      header: 'Key',
      sortValue: (row) => row.key,
      render: (row) => <Mono>{row.key}</Mono>,
    },
    {
      key: 'version',
      header: 'Version',
      align: 'right',
      width: '6rem',
      sortValue: (row) => row.version,
      render: (row) => <span className="font-medium text-ink">v{formatNumber(row.version)}</span>,
    },
    {
      key: 'status',
      header: 'Status',
      width: '8rem',
      sortValue: (row) => row.status,
      render: (row) => <Badge tone={STATUS_TONE[row.status]}>{row.status.toLowerCase()}</Badge>,
    },
    {
      key: 'stepCount',
      header: 'Steps',
      align: 'right',
      width: '5rem',
      sortValue: (row) => row.stepCount,
      render: (row) => formatNumber(row.stepCount),
    },
    {
      key: 'publishedAt',
      header: 'Published',
      hideBelow: 'md',
      sortValue: (row) => row.publishedAt ?? '',
      render: (row) =>
        row.publishedAt ? (
          <time dateTime={row.publishedAt}>{formatDateTime(row.publishedAt)}</time>
        ) : (
          <span className="text-xs text-ink-faint">Not published</span>
        ),
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      hideBelow: 'lg',
      sortValue: (row) => row.updatedAt,
      render: (row) => <time dateTime={row.updatedAt}>{formatDateTime(row.updatedAt)}</time>,
    },
    {
      key: 'actions',
      header: '',
      align: 'right',
      width: '11rem',
      render: (row) => (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="secondary" onClick={() => void navigate(`/wizards/${row.id}`)}>
            {row.status === 'DRAFT' ? 'Edit draft' : 'View'}
          </Button>

          {/* ⚠️ Drafts only, matching the API. A published version is the
              definition some child's answers were recorded against — deleting
              it would leave those answers meaning nothing, which is why the
              endpoint refuses and why the button is not offered. */}
          {row.status === 'DRAFT' ? (
            <Button
              size="sm"
              variant="ghost"
              aria-label={`Discard draft v${String(row.version)}`}
              onClick={() => setDiscarding(row)}
            >
              <Trash2 aria-hidden="true" />
            </Button>
          ) : null}
        </div>
      ),
    },
  ];

  const discardDraft = async (): Promise<void> => {
    if (!discarding) return;

    setDiscardPending(true);
    try {
      await deleteOne({
        resource: RESOURCES.wizards,
        id: discarding.id,
        successNotification: false,
        errorNotification: false,
      });
      notify.success('Draft discarded', `Version ${String(discarding.version)} is gone.`);
      setDiscarding(null);
      invalidate();
    } catch (err) {
      notify.error(err, 'The draft was not discarded');
    } finally {
      setDiscardPending(false);
    }
  };

  return (
    <Page
      title="Wizard builder"
      description="The server-driven form parents fill in to create a child profile."
      breadcrumbs={[{ label: 'Content' }, { label: 'Wizard builder' }]}
      width="wide"
      action={
        <Button variant="primary" loading={creating} onClick={() => void createDraft(primaryKey)}>
          <Plus aria-hidden="true" />
          New draft
        </Button>
      }
    >
      <div className="space-y-5">
        <ConfirmDialog
          open={discarding !== null}
          onOpenChange={(open) => {
            if (!open) setDiscarding(null);
          }}
          title="Discard this draft?"
          description={
            <>
              Version {discarding?.version} and everything in it is deleted. Nothing a parent sees
              changes — a draft has never been served to the app.
            </>
          }
          confirmLabel="Discard draft"
          destructive
          loading={discardPending}
          onConfirm={discardDraft}
        />

        <Notice title="Published versions are read-only">
          Children record the version that produced their answers. Editing a published definition
          would change what those answers mean, so a new draft is created instead.
        </Notice>

        <DataTable
          label="Wizard versions"
          columns={columns}
          rows={rows}
          rowKey={(row) => row.id}
          isLoading={isLoading}
          error={error}
          onRetry={refetch}
          endpoint="GET /admin/wizards"
          empty={
            <EmptyState
              title="No wizard versions yet"
              description="Create the first draft, add steps and fields, then publish it for the app to fetch."
              action={
                <Button variant="primary" onClick={() => void createDraft('child_onboarding')}>
                  <Plus aria-hidden="true" />
                  New draft
                </Button>
              }
            />
          }
        />
      </div>
    </Page>
  );
}
